import {
  Timestamp,
  doc,
  type DocumentData,
  type DocumentReference,
} from 'firebase/firestore'
import { firestore } from '@core/services/firebase/firebase-service'
import type { CommentReply } from '@domain/models/activity-comments'

const USERS_COLLECTION = 'users'

function getCollectionRef(
  collectionName: string,
  id: string,
): DocumentReference<DocumentData> {
  if (!firestore) {
    throw new Error('Firestore is not initialized.')
  }
  return doc(firestore, collectionName, id)
}

function isReferenceLike(value: unknown): value is DocumentReference<DocumentData> {
  if (!value || typeof value !== 'object') {
    return false
  }
  if (!('id' in value) || !('path' in value)) {
    return false
  }

  const maybeRef = value as { id?: unknown; path?: unknown }
  return typeof maybeRef.id === 'string' && typeof maybeRef.path === 'string'
}

function refToId(value: unknown): string | undefined {
  if (!isReferenceLike(value)) {
    return undefined
  }
  return value.id
}

function refsToIds(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined
  }
  return value
    .map((item) => refToId(item))
    .filter((item): item is string => Boolean(item))
}

function idsToRefs(
  ids: string[] | undefined,
): DocumentReference<DocumentData>[] | undefined {
  if (ids === undefined) {
    return undefined
  }
  return ids
    .map((id) => id.trim())
    .filter((id) => id.length > 0)
    .map((id) => getCollectionRef(USERS_COLLECTION, id))
}

function toOptionalDate(value: unknown): Date | undefined {
  if (value instanceof Timestamp) {
    return value.toDate()
  }
  if (value instanceof Date) {
    return value
  }
  return undefined
}

function stripNullish(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== null),
  )
}

export class CommentReplyConverter {
  public static fromFirestore(
    uid: string,
    data: Record<string, unknown>,
  ): CommentReply {
    return {
      uid,
      comment:
        typeof data['comment_reply'] === 'string' ? data['comment_reply'] : undefined,
      postedOn: toOptionalDate(data['posted_on']),
      postedBy: refToId(data['posted_by']),
      likes: typeof data['likes'] === 'number' ? data['likes'] : undefined,
      likedBy: refsToIds(data['liked_by']),
    }
  }

  public static toFirestore(reply: Partial<CommentReply>): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      comment_reply: reply.comment,
      posted_on: reply.postedOn ? Timestamp.fromDate(reply.postedOn) : undefined,
      posted_by: reply.postedBy
        ? getCollectionRef(USERS_COLLECTION, reply.postedBy)
        : undefined,
      likes: reply.likes,
      liked_by: idsToRefs(reply.likedBy),
    }

    return stripNullish(payload)
  }
}
